import { routes } from "@bezoned/shared/routes.js";
import { useQuery } from "@tanstack/react-query";
import { Duration } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { FullScreenLoader } from "../../components/ui/loaders/full-screen-loader.js";
import {
  checkOrganizationPermissions,
  createTenant,
} from "../../services/api.service.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { RetryOrganizationCreation } from "./retry-organization-creation.js";
import { RetryPermissionPropagation } from "./retry-permission-propagation.js";

const PERMISSION_CHECK_INTERVAL_MS = Duration.fromObject({
  seconds: 10,
}).toMillis();
const MAX_PERMISSION_CHECKS = 6;

const wait = (durationMs: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs);
  });

const checkPermissionsUntilReady = async ({
  refetchPermissions,
  onCheck,
}: {
  refetchPermissions: () => Promise<{ data?: unknown; error?: Error | null }>;
  onCheck: (currentCheck: number) => void;
}) => {
  for (
    let currentCheck = 1;
    currentCheck <= MAX_PERMISSION_CHECKS;
    currentCheck++
  ) {
    onCheck(currentCheck);

    const result = await refetchPermissions();

    if (result.data) {
      return { hasPermissions: true, lastError: null };
    }

    if (result.error) {
      return { hasPermissions: false, lastError: result.error };
    }

    if (currentCheck < MAX_PERMISSION_CHECKS) {
      await wait(PERMISSION_CHECK_INTERVAL_MS);
    }
  }

  return { hasPermissions: false, lastError: null };
};

export const TenantCreationInProgress = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [shouldRetryPermissions, setShouldRetryPermissions] = useState(false);
  const [currentPermissionCheck, setCurrentPermissionCheck] = useState(0);
  const { error, isSuccess, refetch } = useQuery({
    enabled: false,
    queryFn: createTenant,
    queryKey: ["createTenant"],
  });

  const { refetch: refetchPermissions } = useQuery({
    enabled: false,
    queryFn: () => checkOrganizationPermissions(),
    queryKey: ["checkOrganizationPermissions"],
    retry: false,
  });
  const hasStartedPermissionChecks = useRef(false);
  const hasHandledSuccess = useRef(false);
  const hasTriggeredCreation = useRef(false);

  const triggerTenantCreation = useCallback(() => {
    if (hasTriggeredCreation.current) {
      return;
    }

    hasTriggeredCreation.current = true;
    refetch();
  }, [refetch]);

  const runPermissionChecks = useCallback(async () => {
    setShouldRetryPermissions(false);
    setIsCheckingPermissions(true);
    const { hasPermissions, lastError } = await checkPermissionsUntilReady({
      onCheck: setCurrentPermissionCheck,
      refetchPermissions,
    });

    setIsCheckingPermissions(false);
    setCurrentPermissionCheck(0);

    if (!hasPermissions) {
      if (lastError) {
        recordErrorInSentry(lastError);
      }
      setShouldRetryPermissions(true);
      return;
    }

    triggerTenantCreation();
  }, [refetchPermissions, triggerTenantCreation]);

  useEffect(() => {
    if (hasStartedPermissionChecks.current) {
      return;
    }

    hasStartedPermissionChecks.current = true;
    const startChecks = async () => {
      try {
        await runPermissionChecks();
      } catch (startError) {
        recordErrorInSentry(startError);
        setIsCheckingPermissions(false);
        setShouldRetryPermissions(true);
      }
    };

    startChecks();
  }, [runPermissionChecks]);

  useEffect(() => {
    if (!isSuccess || hasHandledSuccess.current) {
      return;
    }

    hasHandledSuccess.current = true;
    onSuccess();
    navigate({
      pathname: routes.protected.chooseTeam,
      search: location.search,
    });
  }, [isSuccess, location.search, navigate, onSuccess]);

  const handleRetryPermissions = useCallback(() => {
    runPermissionChecks();
  }, [runPermissionChecks]);

  const handleRetryCreation = useCallback(() => {
    hasTriggeredCreation.current = false;
    triggerTenantCreation();
  }, [triggerTenantCreation]);

  if (error) {
    recordErrorInSentry(error);
    return <RetryOrganizationCreation onRetry={handleRetryCreation} />;
  }

  if (shouldRetryPermissions) {
    return <RetryPermissionPropagation onRetry={handleRetryPermissions} />;
  }

  if (isCheckingPermissions) {
    return (
      <FullScreenLoader
        message={`Checking permissions... (${currentPermissionCheck}/${MAX_PERMISSION_CHECKS})`}
      />
    );
  }

  return <FullScreenLoader message="Creating organization..." />;
};
